import { authHeader } from "./supabase";
import type { DatasetFeatures, SourceProbe } from "./capabilities";

export interface TraceStep {
  t: number;
  ms?: number;
  level: "info" | "warn" | "error";
  scope: string;
  message: string;
  detail?: unknown;
}

export interface TraceEnvelope {
  traceId: string;
  route: string;
  totalMs: number;
  verbose: boolean;
  counters: Record<string, number>;
  steps: TraceStep[];
}

/** Debug mode is a URL flag, so a failing user can be asked to add ?debug=1. */
export const debugMode = () =>
  new URLSearchParams(window.location.search).get("debug") === "1";

/** Last trace seen, for the debug panel, regardless of success or failure. */
export let lastTrace: TraceEnvelope | null = null;

export type ColumnFormat =
  "text" | "int" | "float" | "percent" | "position" | "date" | "signed";

export interface MetricColumn {
  key: string;
  label: string;
  format: ColumnFormat;
  primary?: boolean;
  help: string;
}

export interface EntitySeriesRow {
  week: string;
  impressions: number;
  clicks: number;
  ctr: number | null;
  position: number | null;
  pages?: number;
}

export interface EntitySeries {
  dim: "query" | "url";
  value: string;
  rows: EntitySeriesRow[];
  related: { name: string; impressions: number; clicks: number }[];
  bytes: number;
  estUsd: number;
}

export interface MetricMeta {
  id: string;
  label: string;
  blurb: string;
  render: "timeseries" | "table" | "stacked";
  columns: MetricColumn[];
  xKey?: string;
  seriesKey?: string;
  entity?: "query" | "url";
}

export interface MetricResult {
  status: "done" | "timeout" | "estimate";
  metric?: string;
  columns?: MetricColumn[];
  rows?: Record<string, string | null>[];
  bytes?: number;
  estUsd?: number;
  hint?: string;
}

export interface GscSample {
  kind: "gsc";
  project: string;
  dataset: string;
  location: string | null;
  siteUrl: string | null;
  propertyType: "domain" | "url_prefix" | null;
  topUrls: { url: string; impressions: number; clicks: number }[];
  topQueries: string[];
  impressions28d: number;
  earliestDate: string | null;
  latestDate: string | null;
  anonymizedShare: number | null;
  hasUrlImpression: boolean;
  error?: string;
}

export interface Ga4Sample {
  kind: "ga4";
  project: string;
  dataset: string;
  location: string | null;
  propertyId: string;
  topLandingPages: { page: string; sessions: number }[];
  topHosts: string[];
  sessions28d: number;
  earliestDate: string | null;
  latestDate: string | null;
  hasSessionLastClick: boolean;
  hasIntraday: boolean;
  error?: string;
}

export type Dimension =
  "query" | "page" | "country" | "device" | "appearance" | "searchType" | "date";
export type FilterOp = "is" | "contains" | "notContains" | "startsWith";

export interface Filter { dim: Dimension; op: FilterOp; value: string }

export interface ExploreRow {
  key: string;
  clicks: number;
  impressions: number;
  ctr: number | null;
  position: number | null;
  prevClicks?: number;
  prevImpressions?: number;
  prevCtr?: number | null;
  prevPosition?: number | null;
}

export interface ExploreResult {
  totals: {
    clicks: number; impressions: number; ctr: number | null; position: number | null;
    withheldImpressions: number; withheldClicks: number; distinct: number;
  };
  series: { date: string; clicks: number; impressions: number;
            ctr: number | null; position: number | null }[];
  compareSeries?: { date: string; clicks: number; impressions: number;
                    ctr: number | null; position: number | null }[];
  compareTotals?: {
    clicks: number; impressions: number; ctr: number | null; position: number | null;
  };
  rows: ExploreRow[];
  truncated: boolean;
  bytes: number;
  estUsd: number;
  /** Served from cache, so it cost nothing and took no BigQuery scan. */
  cached?: boolean;
  /** Simulated. Never real data. */
  demo?: boolean;
}

export type Ga4Dimension = "landingPage" | "sourceMedium" | "device" | "country" | "date";

export interface FunnelStage { name: string; value: number; rate: number | null }

export interface Ga4Row {
  key: string;
  sessions: number;
  engaged: number;
  keyEvents: number;
  revenue: number;
  engagementRate: number | null;
  conversionRate: number | null;
  avgEngagementSeconds: number | null;
}

export interface Ga4Result {
  stages: FunnelStage[];
  rows: Ga4Row[];
  series: { date: string; sessions: number; engaged: number; keyEvents: number }[];
  eventNames: { name: string; count: number }[];
  hasSessionLastClick: boolean;
  distinct: number;
  bytes: number;
  estUsd: number;
  cached?: boolean;
  demo?: boolean;
}

export interface BridgeRow {
  key: string;
  impressions: number;
  clicks: number;
  ctr: number | null;
  position: number | null;
  estSessions: number;
  estEngaged: number;
  estKeyEvents: number;
  estRevenue: number;
  clickToSession: number | null;
  sessionToKeyEvent: number | null;
  isWithheld: boolean;
}

export interface BridgeHealth {
  ratioP50: number | null;
  ratioP10: number | null;
  ratioP90: number | null;
  matchedPageDays: number;
  unmatchedPageDays: number;
  overlapDays: number;
  gscClicks: number;
  ga4Sessions: number;
  withheldClickShare: number | null;
}

export interface BridgeResult {
  rows: BridgeRow[];
  health: BridgeHealth;
  bytes: number;
  estUsd: number;
  cached?: boolean;
  demo?: boolean;
}

export interface PropertySource {
  property_id: string;
  kind: "gsc_bq" | "ga4_bq" | "gsc_api" | "ga4_api";
  connection_id: string | null;
  gsc_site_url: string | null;
  ga4_property_id: string | null;
  bq_project: string | null;
  bq_dataset: string | null;
  bq_location: string | null;
  earliest_date?: string | null;
  lag_days?: number | null;
}

export interface Property {
  id: string;
  name: string;
  site_domain: string | null;
  sources: PropertySource[];
}

export interface GbpLocation {
  resourceName: string;
  title: string;
  address: string | null;
  primaryPhone: string | null;
  websiteUri: string | null;
}

export interface GbpAccount {
  account: { name: string; accountName: string; type: string };
  locations: GbpLocation[];
}

export interface LinkedGbpLocation {
  id: string;
  title: string;
  resource_name: string;
}

export interface Me {
  user: { id: string; email: string };
  workspaces: { id: string; name: string; slug: string; role: string }[];
  connections: { id: string; google_email: string; status: string; last_verified: string | null }[];
}

export interface SavedSelection {
  connectionId: string;
  gsc: SourceRef | null;
  ga4: SourceRef | null;
  label: string;
}

export interface ProjectOutcome {
  id: string;
  name: string;
  scanned: boolean;
  status: "ok" | "no_bigquery" | "error" | "skipped";
  datasetCount?: number;
  detail?: string;
}

export interface DiscoverResponse {
  projects: ProjectOutcome[];
  gsc: GscSample[];
  ga4: Ga4Sample[];
  warnings: string[];
}

export interface SourceRef {
  project: string;
  dataset: string;
  location: string | null;
  siteUrl?: string | null;
}

export interface ConnectResponse {
  probes: SourceProbe[];
  features: DatasetFeatures;
  warnings: string[];
  choices: {
    projects: { id: string; name: string }[];
    gscSites: { siteUrl: string; permission: string }[];
    ga4Properties: { propertyId: string; displayName: string; account: string }[];
    gscDatasets: string[];
    ga4Datasets: string[];
    keyEventCandidates: { name: string; count: number }[];
  };
  nextStep:
    | { kind: "ready" }
    | { kind: "pick_property" }
    | { kind: "pick_project"; reason: string }
    | { kind: "no_access"; reason: string };
}

export interface ProbeResponse {
  probes: SourceProbe[];
  features: DatasetFeatures;
  candidates: { gscDatasets: string[]; ga4Datasets: string[] };
  warnings: string[];
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = debugMode() ? `${path}${path.includes("?") ? "&" : "?"}debug=1` : path;

  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(debugMode() ? { "X-Debug": "1" } : {}),
        ...(await authHeader()),
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    // Network-level failure: the worker was never reached.
    throw new ApiError(0, {
      error: "network",
      message: String(e),
      hint: "The request never reached the worker. Check the deployment is live and the route is bound to this domain.",
    });
  }

  const text = await res.text();
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    // HTML back from an API path almost always means routing sent it to the SPA.
    throw new ApiError(res.status, {
      error: "non_json_response",
      contentType: res.headers.get("Content-Type"),
      preview: text.slice(0, 300),
      hint: "The API path returned HTML. The worker route is probably not covering /api on this domain.",
    });
  }

  if (body && typeof body === "object" && "_trace" in body) {
    lastTrace = (body as { _trace: TraceEnvelope })._trace;
  }

  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`request failed (${status})`);
  }
  /** Server-supplied hint, which is usually the actual fix. */
  get hint(): string | null {
    const b = this.body as { hint?: string } | null;
    return b?.hint ?? null;
  }
  get code(): string | null {
    const b = this.body as { error?: string } | null;
    return b?.error ?? null;
  }
  get trace(): TraceEnvelope | null {
    const b = this.body as { _trace?: TraceEnvelope } | null;
    return b?._trace ?? null;
  }
  get needsReauth() {
    return typeof this.body === "object" && this.body !== null &&
      (this.body as { error?: string }).error === "reauth_required";
  }
}

export const api = {
  health: () =>
    call<Record<string, unknown>>("/api/health"),

  authUrl: (workspaceId: string) =>
    call<{ url: string }>("/api/google/auth-url", {
      method: "POST", body: JSON.stringify({ workspace_id: workspaceId }),
    }),

  bootstrap: () =>
    call<{ workspaces: { id: string; name: string; slug: string }[] }>(
      "/api/bootstrap", { method: "POST", body: "{}" }),



  metrics: () =>
    call<{ metrics: MetricMeta[] }>("/api/metrics"),

  metric: (body: {
    connection_id: string; metric: string; project: string; dataset: string;
    location?: string | null; from: string; to: string; estimate_only?: boolean;
  }) => call<MetricResult>("/api/metric", { method: "POST", body: JSON.stringify(body) }),

  me: () => call<Me>("/api/me"),

  bridge: (body: {
    connection_id: string; project: string; gscDataset: string; ga4Dataset: string;
    location?: string | null; from: string; to: string;
    keyEvents: string[]; dimension: "query" | "page";
    hasSessionLastClick?: boolean; limit: number; demo?: boolean;
  }) => call<BridgeResult>("/api/bridge", { method: "POST", body: JSON.stringify(body) }),

  ga4Explore: (body: {
    connection_id: string; project: string; dataset: string; location?: string | null;
    from: string; to: string; dimension: Ga4Dimension;
    channel: "all" | "organic"; keyEvents: string[];
    landingPageContains?: string; limit: number;
    has_session_last_click?: boolean; demo?: boolean;
  }) => call<Ga4Result>("/api/ga4/explore", { method: "POST", body: JSON.stringify(body) }),

  gbpDiscover: (connectionId: string) =>
    call<{ accounts: GbpAccount[] }>("/api/gbp/discover", {
      method: "POST", body: JSON.stringify({ connection_id: connectionId }),
    }),

  gbpLink: (body: {
    property_id: string; connection_id: string;
    locations: (GbpLocation & { account: string })[];
  }) => call<{ locations: LinkedGbpLocation[] }>("/api/gbp/link", {
    method: "POST", body: JSON.stringify(body),
  }),

  gbpSync: (body: {
    connection_id: string; location_id: string; from: string; to: string;
  }) => call<{ days: number; keywords: number }>("/api/gbp/sync", {
    method: "POST", body: JSON.stringify(body),
  }),

  gbpSeries: (propertyId: string, from: string, to: string) =>
    call<{
      locations: LinkedGbpLocation[];
      series: Record<string, string | number>[];
      keywords: Record<string, string | number | boolean>[];
    }>(`/api/gbp/series?property_id=${propertyId}&from=${from}&to=${to}`),

  explore: (body: {
    connection_id: string; project: string; dataset: string; location?: string | null;
    from: string; to: string; dimension: Dimension; filters: Filter[];
    grain: "day" | "week"; limit: number;
    compare?: { from: string; to: string } | null;
    demo?: boolean;
  }) => call<ExploreResult>("/api/explore", { method: "POST", body: JSON.stringify(body) }),

  listProperties: (workspaceId: string) =>
    call<{ properties: Property[] }>(`/api/properties?workspace_id=${workspaceId}`),

  saveProperty: (body: {
    workspace_id: string; connection_id: string; name: string;
    property_id?: string; site_domain?: string | null;
    gsc?: SourceRef | null; ga4?: SourceRef | null;
  }) => call<{ property_id: string }>("/api/properties", {
    method: "POST", body: JSON.stringify(body),
  }),

  deleteProperty: (id: string) =>
    call<{ deleted: string }>(`/api/properties?id=${id}`, { method: "DELETE" }),

  entity: (body: {
    connection_id: string; project: string; dataset: string;
    location?: string | null; dim: "query" | "url"; value: string;
    from: string; to: string;
  }) => call<EntitySeries>("/api/entity", { method: "POST", body: JSON.stringify(body) }),

  discover: (connectionId: string, projectIds?: string[]) =>
    call<DiscoverResponse>("/api/discover", {
      method: "POST",
      body: JSON.stringify({ connection_id: connectionId, project_ids: projectIds }),
    }),

  connect: (body: Record<string, string>) =>
    call<ConnectResponse>("/api/connect", { method: "POST", body: JSON.stringify(body) }),

  projects: (connectionId: string) =>
    call<{ projects: { id: string; name: string }[] }>(
      `/api/google/projects?connection_id=${connectionId}`),

  gscSites: (connectionId: string) =>
    call<{ sites: { siteUrl: string; permission: string }[] }>(
      `/api/google/gsc-sites?connection_id=${connectionId}`),

  probe: (body: { connection_id: string; project_id: string; gsc_dataset?: string; ga4_dataset?: string }) =>
    call<ProbeResponse>("/api/probe", { method: "POST", body: JSON.stringify(body) }),

  estimate: (body: { connection_id: string; project_id: string; sql: string; location?: string }) =>
    call<{ bytes: number; estUsd: number }>("/api/estimate", { method: "POST", body: JSON.stringify(body) }),

  run: (body: { connection_id: string; project_id: string; sql: string; location?: string }) =>
    call<{ status: string; rows?: Record<string, string | null>[]; bytes?: number; estUsd?: number; jobId?: string }>(
      "/api/run", { method: "POST", body: JSON.stringify(body) }),
};
